import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { BaseService } from "./base.service";
import { ICountryConfigSet } from "../interfaces/icountryconfigset";
import { IItemConfigSet, IItemConfig } from "../interfaces/iitemconfig";
import { IAppStore } from "../iappstore";
import { Store } from "@ngrx/store";

/**
 * @whatItDoes A service for anything item related
 *
 * @howToUse Inject the service
 */
@Injectable()
export class ItemConfigService extends BaseService {
    /**
     * Constructor for service
     * @param httpClient angular http module
     * @param toastr toastr manager
     * @param store Store
     */
    constructor(httpClient: HttpClient, toastr: ToastrService, store: Store<IAppStore>) {
        super(httpClient, toastr, store);
    }

    /**
     * Returns an observable config set
     */
    getConfigSet(): Observable<IItemConfigSet> {
        return super.get("/api/config").pipe(
            map((res: any) => res)
        );
    }

    /**
     * Returns an observable config set
     */
    getConfigSetByCountry(countryCode: string): Observable<ICountryConfigSet> {
        return super.get(`/api/config/${countryCode}`).pipe(
            map((res: any) => res as ICountryConfigSet)
        );
    }

    /**
     * Returns an observable config set
     */
    getConfigSetByCountryAndItemConfigId(countryCode: string, parentItemConfigId: number): Observable<IItemConfigSet> {
        return super.get(`/api/config/${countryCode}/${parentItemConfigId}`).pipe(
            map((res: any) => res as IItemConfigSet)
        );
    }

    /**
     * Returns an observable of a item for the provided id
     * @param id The item id to retrieve
     */
    getItem(id: number): Observable<IItemConfig> {
        return super.get(`/api/itemconfig/${id}`).pipe(
            map((res: any) => res as IItemConfig)
        );
    }

    /**
     * Returns an observable of the item created
     * @param item The item to create
     */
    create(item: IItemConfig): Observable<IItemConfig> {
        return super.post("/api/itemconfig", item).pipe(
            map((res: any) => res as IItemConfig)
        );
    }

    /**
     * Returns an observable of the updated item
     * @param item The item to update
     */
    update(item: IItemConfig): Observable<IItemConfig> {
        return super.post("/api/itemconfig/updateitem", item).pipe(
            map((res: any) => res as IItemConfig)
        );
    }

    save(item: IItemConfig): Observable<IItemConfig> {
        if (!item.id) {
            return this.create(item);
        } else {
            return this.update(item);
        }
    }

    updateItems(items: IItemConfig[]): Observable<IItemConfig[]> {
        return super.put("/api/itemconfig/items", items).pipe(
            map((res: any) => res as IItemConfig[])
        );
    }
}
